import {
  HTTP_HEADER_AUTH_VERSION,
  HTTP_HEADER_ROOM,
  HTTP_HEADER_DEVICE,
  HTTP_HEADER_TIMESTAMP,
  HTTP_HEADER_NONCE,
  HTTP_HEADER_BODY_SHA256,
  HTTP_HEADER_SIGNATURE,
  PRODUCTION_DEVICE_AUTH_VERSION,
} from "@echopaste/protocol";
import {
  sha256Base64Url,
  signDeviceRequest,
  uint8ArrayToBase64Url,
  type ProductionDeviceIdentity,
} from "@echopaste/crypto";

export interface ProductionAuthDeps {
  now?: () => number;
  randomBytes?: (length: number) => Uint8Array;
  fetch?: typeof fetch;
}

export interface SignedProductionFetchArgs {
  apiBase: string;
  identity: ProductionDeviceIdentity;
  roomId: string;
  path: string;
  method: string;
  body?: string | Uint8Array;
  headers?: HeadersInit;
}

export interface CreateProductionWebSocketUrlArgs {
  wsOrigin: string;
  identity: ProductionDeviceIdentity;
  roomId: string;
}

function rfc3986Encode(value: string): string {
  return encodeURIComponent(value).replace(/[!'()*]/g, (character) => `%${character.charCodeAt(0).toString(16).toUpperCase()}`);
}

/**
 * Performs a signed HTTP fetch for production device requests.
 * Uses the non-extractable local P-256 signing key to sign canonical request fields.
 * Signs exact submitted raw body bytes and injects fixed production auth headers.
 */
export async function signedProductionFetch(
  args: SignedProductionFetchArgs,
  deps?: ProductionAuthDeps
): Promise<Response> {
  const { apiBase, identity, roomId, path, method, body, headers } = args;

  if (!identity || !identity.signingPrivateKey || !identity.deviceId) {
    throw new Error("Production device identity with signing key is required");
  }
  if (!roomId || typeof roomId !== "string") {
    throw new Error("Target roomId is required");
  }
  if (!path.startsWith("/") || path.includes("?") || path.includes("#")) {
    throw new Error(`Invalid signed request path: ${path}`);
  }

  const upperMethod = method.toUpperCase();
  const cleanBase = apiBase.replace(/\/+$/, "");
  const fullUrl = `${cleanBase}${path}`;

  let rawBody: Uint8Array;
  let bodyToSend: BodyInit | undefined;
  if (body === undefined || body === null) {
    rawBody = new Uint8Array(0);
    bodyToSend = undefined;
  } else if (typeof body === "string") {
    rawBody = new TextEncoder().encode(body);
    bodyToSend = body;
  } else if (body instanceof Uint8Array) {
    rawBody = body;
    bodyToSend = body as unknown as BodyInit;
  } else {
    throw new Error("Unsupported body type for signed production fetch");
  }

  const bodySha256 = await sha256Base64Url(rawBody);

  const nowFn = deps?.now ?? Date.now;
  const timestamp = nowFn();
  if (!Number.isSafeInteger(timestamp) || timestamp < 0) {
    throw new Error("Invalid timestamp for signed production fetch");
  }

  const randomFn = deps?.randomBytes ?? ((len: number) => crypto.getRandomValues(new Uint8Array(len)));
  const nonceBytes = randomFn(32);
  const nonce = uint8ArrayToBase64Url(nonceBytes);

  const signature = await signDeviceRequest(identity.signingPrivateKey, {
    version: PRODUCTION_DEVICE_AUTH_VERSION,
    method: upperMethod,
    path,
    query: "",
    roomId,
    deviceId: identity.deviceId,
    timestamp,
    nonce,
    bodySha256,
  });

  const reqHeaders = new Headers(headers);
  // Authoritative signer headers overwrite any caller-supplied spoofed values
  reqHeaders.set(HTTP_HEADER_AUTH_VERSION, String(PRODUCTION_DEVICE_AUTH_VERSION));
  reqHeaders.set(HTTP_HEADER_ROOM, roomId);
  reqHeaders.set(HTTP_HEADER_DEVICE, identity.deviceId);
  reqHeaders.set(HTTP_HEADER_TIMESTAMP, String(timestamp));
  reqHeaders.set(HTTP_HEADER_NONCE, nonce);
  reqHeaders.set(HTTP_HEADER_BODY_SHA256, bodySha256);
  reqHeaders.set(HTTP_HEADER_SIGNATURE, signature);

  const fetchFn = deps?.fetch ?? fetch;
  return await fetchFn(fullUrl, {
    method: upperMethod,
    headers: reqHeaders,
    body: bodyToSend,
  });
}

/**
 * Creates an authorized WebSocket URL containing Task 4 query proof.
 * Uses the non-extractable local P-256 signing key to sign canonical GET /ws request.
 */
export async function createProductionWebSocketUrl(
  args: CreateProductionWebSocketUrlArgs,
  deps?: ProductionAuthDeps
): Promise<string> {
  const { wsOrigin, identity, roomId } = args;

  if (!identity || !identity.signingPrivateKey || !identity.deviceId) {
    throw new Error("Production device identity with signing key is required");
  }
  if (!roomId || typeof roomId !== "string") {
    throw new Error("Target roomId is required");
  }

  const cleanOrigin = wsOrigin.replace(/\/+$/, "");

  const nowFn = deps?.now ?? Date.now;
  const timestamp = nowFn();
  if (!Number.isSafeInteger(timestamp) || timestamp < 0) {
    throw new Error("Invalid timestamp for production WebSocket URL");
  }

  const randomFn = deps?.randomBytes ?? ((len: number) => crypto.getRandomValues(new Uint8Array(len)));
  const nonceBytes = randomFn(32);
  const nonce = uint8ArrayToBase64Url(nonceBytes);

  const emptyBody = new Uint8Array(0);
  const bodySha256 = await sha256Base64Url(emptyBody);

  const canonicalEntries: [string, string][] = [
    ["authVersion", String(PRODUCTION_DEVICE_AUTH_VERSION)],
    ["bodySha256", bodySha256],
    ["deviceId", identity.deviceId],
    ["nonce", nonce],
    ["roomId", roomId],
    ["timestamp", String(timestamp)],
  ];

  const canonicalQuery = canonicalEntries
    .map(([k, v]) => `${rfc3986Encode(k)}=${rfc3986Encode(v)}`)
    .join("&");

  const signature = await signDeviceRequest(identity.signingPrivateKey, {
    version: PRODUCTION_DEVICE_AUTH_VERSION,
    method: "GET",
    path: "/ws",
    query: canonicalQuery,
    roomId,
    deviceId: identity.deviceId,
    timestamp,
    nonce,
    bodySha256,
  });

  const queryParts = [
    `authVersion=${encodeURIComponent(String(PRODUCTION_DEVICE_AUTH_VERSION))}`,
    `roomId=${encodeURIComponent(roomId)}`,
    `deviceId=${encodeURIComponent(identity.deviceId)}`,
    `timestamp=${encodeURIComponent(String(timestamp))}`,
    `nonce=${encodeURIComponent(nonce)}`,
    `bodySha256=${encodeURIComponent(bodySha256)}`,
    `signature=${encodeURIComponent(signature)}`,
  ];

  return `${cleanOrigin}/ws?${queryParts.join("&")}`;
}
